import { getResourcePermissionsByRole } from '../../../permission/utils/getResourcePermissionsByRole.js';
import { isResourceDetailFolder, isResourceIntegrationItem } from '../../../resources/utils/resourceDetailGuards.js';
import { PermissionRole, RootType } from '@fabric/woody-client';
import { useMemo } from 'react';

const getMoveDisabledReason = (resource, permissions) => {
  if (!permissions.resource.remove.allowed) {
    return permissions.resource.remove.info;
  }
  if (resource.isDraft) {
    return "Can't be moved while saving";
  }
  if (isResourceIntegrationItem(resource)) {
    return "This item is synced via connection and can't be moved";
  }
  return null;
};
const getModifyDisabledReason = (resource, permissions) => {
  if (resource.isDraft) {
    return null;
  }
  if (!permissions.resource.edit.allowed) {
    return permissions.resource.edit.info;
  }
  return null;
};
const getModifyLabelDisabledReason = (resource, permissions) => {
  if (!permissions.resource.edit.allowed) {
    return permissions.resource.edit.info;
  }
  if (resource.isDraft) {
    return "Can't change label while saving.";
  }
  return null;
};
const getRemoveDisabledReason = (resource, permissions) => {
  if (!permissions.resource.remove.allowed) {
    return permissions.resource.remove.info;
  }
  if (resource.isDraft) {
    return "Can't be delete while saving.";
  }
  if (isResourceIntegrationItem(resource)) {
    return "This item is synced via connection and can't be deleted.";
  }
  return null;
};
const getLeaveDisabledReason = (resource) => {
  if (!resource.permission) {
    return "Not shared";
  }
  if (resource.permission.role === PermissionRole.OWNER) {
    return "Cannot leave own item";
  }
  if (!resource.permission.hasAddedDirectly) {
    return "Cannot leave. This item has been added to your library through a folder or space";
  }
  if (isResourceIntegrationItem(resource)) {
    return "This item is synced via connection.";
  }
  return null;
};
const getAddResourcesDisabledReason = (resource, permissions) => {
  if (resource.isDraft) {
    return "Can't add items while creating.";
  }
  if (!isResourceDetailFolder(resource)) {
    return "Not a folder";
  }
  if (isResourceIntegrationItem(resource)) {
    return "Can't add items into a folder synced via connection.";
  }
  if (!permissions.childResources.create.allowed) {
    return permissions.childResources.create.info;
  }
  return null;
};
const getMoveOrDeleteResourcesDisabledReason = (resource, permissions) => {
  if (!isResourceDetailFolder(resource)) {
    return "Not a folder";
  }
  if (isResourceIntegrationItem(resource)) {
    return "Can't add move or delete items from a folder synced via connection.";
  }
  if (!permissions.childResources.edit.allowed) {
    return permissions.childResources.edit.info;
  }
  return null;
};
const getAddCommentDisabledReason = (resource, permissions) => {
  if (!permissions.addOrEditOwnComment.allowed) {
    return permissions.addOrEditOwnComment.info;
  }
  return null;
};
const getManageOthersCommentsDisabledReason = (resource, permissions) => {
  if (!permissions.comments.edit.allowed) {
    return permissions.comments.edit.info;
  }
  return null;
};
const getViewCommentsDisabledReason = (resource, permissions) => {
  if (!permissions.comments.edit.allowed) {
    return permissions.comments.edit.info;
  }
  return null;
};
const getCanManageSomePermissionROles = (resource, permissions) => {
  if (resource.root?.type === RootType.INTEGRATION) {
    return "Items synced via connection can't be shared";
  }
  if (!permissions.permission.canGrantPermissions) {
    return "Can't manage sharing";
  }
  return null;
};
const getPublishItemDisabledReason = (resource, permissions) => {
  if (!permissions.permission.canEditPublishingSettings) {
    return "You don't have permission to publish this item";
  }
  return null;
};
const getDisabledActionsReasons = (resource, permissions) => {
  if (!resource || !permissions) {
    return {
      move: "Can't move",
      leave: "Can't leave",
      remove: "Can't delete",
      modifyLabel: "Can't edit label",
      modify: "Can't rename",
      createChildResources: "Can't create or remove",
      modifyChildResources: "Can't move or delete",
      manageSomePermissionRoles: "Can't manage sharing",
      addOrEditOwnComment: "Can't comment",
      manageOthersComments: "Can't manage comments",
      viewComments: "Can't view comments",
      publish: "You don't have permission to publish this item"
    };
  }
  return {
    move: getMoveDisabledReason(resource, permissions),
    leave: getLeaveDisabledReason(resource),
    remove: getRemoveDisabledReason(resource, permissions),
    modifyLabel: getModifyLabelDisabledReason(resource, permissions),
    modify: getModifyDisabledReason(resource, permissions),
    createChildResources: getAddResourcesDisabledReason(resource, permissions),
    modifyChildResources: getMoveOrDeleteResourcesDisabledReason(resource, permissions),
    manageSomePermissionRoles: getCanManageSomePermissionROles(resource, permissions),
    addOrEditOwnComment: getAddCommentDisabledReason(resource, permissions),
    manageOthersComments: getManageOthersCommentsDisabledReason(resource, permissions),
    viewComments: getViewCommentsDisabledReason(resource, permissions),
    publish: getPublishItemDisabledReason(resource, permissions)
  };
};
const getResourcePermissionsAndDisabledActions = (resource) => {
  const permissions = getResourcePermissionsByRole(resource?.permission);
  const disabledActions = getDisabledActionsReasons(resource, permissions);
  const userRole = resource?.permission?.role || PermissionRole.VIEWER;
  return {
    disabledActions,
    permissions,
    userRole
  };
};
const getMultipleResourcesLeaveOrDeleteActionDetails = (resources) => {
  const perms = (resources || []).map((item) => ({
    ...item,
    pd: getResourcePermissionsAndDisabledActions(item)
  }));
  const isAllIntegrationItems = resources.length > 0 && resources.every(isResourceIntegrationItem);
  if (isAllIntegrationItems) {
    return {
      moveDisabledReason: "Can't move items synced via connection",
      deleteOrLeaveDisabledReason: "Can't delete items synced via connection",
      deleteOrLeaveLabel: "Delete",
      itemsToLeave: [],
      itemsToRemove: []
    };
  }
  const moveDisabled = perms.some((item) => !!item.pd.disabledActions.move);
  const deleteDisabled = perms.some((item) => !!item.pd.disabledActions.remove);
  const deleteOrLeaveDisabled = perms.some((item) => !!item.pd.disabledActions.leave && !!item.pd.disabledActions.remove);
  const leaveDisabled = perms.some((item) => !!item.pd.disabledActions.leave);
  const itemsToLeave = perms.filter((item) => !item.pd.disabledActions.leave);
  const itemsToRemove = perms.filter((item) => !item.pd.disabledActions.remove);
  return {
    moveDisabledReason: moveDisabled ? "You don't have permission to move some of the selected items" : void 0,
    deleteOrLeaveDisabledReason: deleteOrLeaveDisabled ? leaveDisabled ? "Some items cannot be removed or left" : leaveDisabled ? "You don't have permission to delete some of the selected items" : "Some items cannot be left" : void 0,
    deleteOrLeaveLabel: !leaveDisabled ? "Leave" : !deleteDisabled ? "Delete" : !deleteOrLeaveDisabled ? "Delete/leave" : "Delete",
    itemsToLeave,
    itemsToRemove
  };
};
const useResourcePermissionsByCurrentUser = (resource) => {
  return useMemo(() => {
    return getResourcePermissionsAndDisabledActions(resource);
  }, [
    resource
  ]);
};

export { getMultipleResourcesLeaveOrDeleteActionDetails, getResourcePermissionsAndDisabledActions, useResourcePermissionsByCurrentUser };
//# sourceMappingURL=getDisabledActionsReasons.js.map
